import { useId, type InputHTMLAttributes } from 'react';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string | null;
  hint?: string;
}

// Airbnb input — warm borders, near-black focus ring
export function Input({ label, error, hint, id, className = '', ...rest }: InputProps) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const errorId = error ? `${inputId}-error` : undefined;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;
  const borderColor = error
    ? 'border-error focus:border-error focus:ring-2 focus:ring-error/20'
    : 'border-surface-border focus:border-ink focus:ring-2 focus:ring-ink/10';
  return (
    <div className="block text-body">
      {label && (
        <label htmlFor={inputId} className="mb-1.5 block text-small font-medium text-ink-muted">
          {label}
        </label>
      )}
      <input
        id={inputId}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={describedBy}
        className={`min-h-[48px] w-full rounded-2xl border bg-surface px-4 py-2.5 text-ui text-ink outline-none transition-all duration-200 ease-airbnb placeholder:text-ink-muted/60 ${borderColor} ${className}`}
        {...rest}
      />
      {hint && !error && (
        <p id={hintId} className="mt-1.5 text-small text-ink-muted">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="mt-1.5 text-small font-medium text-error">
          {error}
        </p>
      )}
    </div>
  );
}
